/**
 * Reusable resolution validation utility.
 *
 * Accepts resolutions in the form `<width>x<height>` (case-insensitive `x`),
 * optionally surrounded by spaces, e.g. "1920x1080", "1080 X 1920", "3840x2160".
 * Each dimension must be a positive integer (no leading zero) of 2–5 digits.
 */

// 2–5 digit positive integers separated by `x` / `X`, tolerant of surrounding spaces.
export const RESOLUTION_REGEX = /^[1-9]\d{1,4}\s*[xX]\s*[1-9]\d{1,4}$/;

/**
 * Normalise a resolution string to the canonical `WIDTHxHEIGHT` form
 * (trimmed, lowercase `x`, no inner spaces). Returns the trimmed input
 * unchanged when it does not match the expected pattern.
 * @param {string} value
 * @returns {string}
 */
export const normalizeResolution = (value) => {
  const trimmed = (value ?? "").trim();
  if (!RESOLUTION_REGEX.test(trimmed)) return trimmed;
  return trimmed.replace(/\s*[xX]\s*/, "x");
};

/**
 * Validate a single resolution value.
 * @param {string} value - the raw input value.
 * @param {{ required?: boolean, label?: string }} [options]
 * @returns {string} an error message, or "" when valid.
 */
export const validateResolution = (value, options = {}) => {
  const { required = false, label = "Resolution" } = options;
  const trimmed = (value ?? "").trim();

  if (!trimmed) {
    return required ? `${label} is required` : "";
  }
  if (!RESOLUTION_REGEX.test(trimmed)) {
    return `Enter a valid ${label.toLowerCase()} like 1920x1080`;
  }
  return "";
};
